import React, { useMemo } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import DoneIcon from '@mui/icons-material/Done';
import LoopIcon from '@mui/icons-material/Loop';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import { Box, CircularProgress, Tooltip } from '@mui/material';

import { TooltipIconButton } from '@paragon/client-tools';
import { LayoutModel, ModelCategory } from '@paragon/contract-library';
import { DataRow, PathUtilities } from '@paragon/layout-models';

import { checkComponentDesignsBySealStatus } from '../../store/actions/projectGrid';
import { computeSealingInformation } from '../../store/selectors/sealingInformation';
import { useAppDispatch, useAppSelector } from '../hooks';

function SealStatusIcon(props: { jobId: number; returned?: boolean; date: Date }) {
  return (
    <span>
      <Tooltip
        disableInteractive
        title={
          <>
            Seal {props.returned ? 'Returned' : 'Requested'}{' '}
            {props.date.toLocaleString(undefined, { month: 'numeric', day: 'numeric', year: 'numeric' })}
            <br />
            Job ID: {props.jobId}
          </>
        }
        placement="right"
      >
        {props.returned ? <DoneIcon /> : <LoopIcon />}
      </Tooltip>
    </span>
  );
}

export function ProjectGridSealedCell(props: { readonly row: DataRow<LayoutModel> }) {
  const { row } = props;

  const dispatch = useAppDispatch();
  const projectGuid = useMemo(() => PathUtilities.getProjectGuid(row.rowId), [row.rowId]);
  const loading = useAppSelector((state) => state.grid.loadingSealsForProjects.includes(projectGuid));
  const analyses = useAppSelector((state) => state.analysisHistory[row.item.guid]);
  const sealTimestampsRecord = useAppSelector((state) => state.componentDesignSealTimestamps[row.item.guid]);
  const timestampsRecord = useAppSelector((state) => state.componentDesignTimestamps[row.item.guid]);

  if (row.item.category === ModelCategory.Project) {
    return (
      <Box
        component="span"
        sx={{ display: 'inline-flex', flexWrap: 'nowrap' }}
        onClick={(event) => event.stopPropagation()}
      >
        <TooltipIconButton
          title="Check Unrequested"
          placement="top"
          size="small"
          onClick={() => void dispatch(checkComponentDesignsBySealStatus(row.item.guid, 'none'))}
          disabled={loading}
        >
          <NotInterestedIcon fontSize="small" />
        </TooltipIconButton>
        <TooltipIconButton
          title="Check Sealed"
          placement="top"
          size="small"
          onClick={() => void dispatch(checkComponentDesignsBySealStatus(row.item.guid, 'sealed'))}
          disabled={loading}
        >
          <CheckCircleIcon fontSize="small" />
        </TooltipIconButton>
      </Box>
    );
  }

  if (!row.parent || row.item.category !== ModelCategory.ComponentDesign) return null;

  if (loading)
    return (
      <span>
        <Tooltip disableInteractive title="Loading..." placement="right">
          <CircularProgress size={28} />
        </Tooltip>
      </span>
    );

  const sealingInformation = computeSealingInformation(analyses, sealTimestampsRecord, timestampsRecord);

  if (sealingInformation.sealStatus === 'none') return null;

  return (
    <SealStatusIcon
      jobId={sealingInformation.jobId}
      returned={sealingInformation.sealStatus === 'sealed'}
      date={sealingInformation.date}
    />
  );
}
